// useCondensedPlayer — drives the "Condense Player" mode used by
// InAppMoviePlayer, InAppVideoPlayer and AudioVisualizerPlayer.
//
// When the user scrolls past the pinned player frame into the
// Judgments thread, the player auto-shrinks to a compact strip so the
// comments get the full screen real estate. Tapping the strip (or
// scrolling back up to the top) expands it back to full size.
//
// Why a hook (and not just CSS scroll-driven animations): we need the
// strip overlay to be clickable to expand back — that requires a
// real boolean we can drive both via scroll AND via user tap. Pure
// CSS scroll animations don't give us that.
//
// API:
//   const scrollRef = useRef(null);
//   const { condensed, expand, collapse } = useCondensedPlayer(scrollRef);
//   ...
//   <div ref={scrollRef} className="overflow-y-auto">
//     <PlayerFrame className={condensed ? "h-16" : "h-[62vh]"} />
//     {condensed && <CondensedStrip onTap={expand} ... />}
//   </div>
//
//   • `collapseThreshold` — scrollTop in px at which to auto-collapse
//     (default 320, ~halfway through a 60vh frame on a phone).
//   • `expandThreshold`   — scrollTop in px below which to auto-expand
//     (default 120). Hysteresis prevents flicker right at the boundary.
//   • `userOverrideMs`    — after the user manually expands/collapses,
//     we suppress auto behavior for this long so they don't fight the
//     scroll listener (default 4 s).
import { useState } from "react";

export default function useCondensedPlayer(scrollRef, {
  collapseThreshold = 320,
  expandThreshold = 120,
  userOverrideMs = 4000,
} = {}) {
  // Auto-condense behavior disabled — user feedback (Feb 2026): "when I
  // scroll up on black American films and movies I can't see the movie".
  // The auto-shrink to a 68px strip made the picture vanish the moment
  // they scrolled to reach Judgments. Now the picture stays sticky at
  // full size while Judgments + Up Next scroll underneath, so the movie
  // is always visible. The hook still exposes the same API (condensed
  // boolean + expand/collapse) but condensed is permanently false.
  const [condensed] = useState(false);

  const expand = () => {};
  const collapse = () => {};

  return { condensed, expand, collapse };
}
